import BREAKPOINTS from '@mlb/react-patterns/v4/style/breakpoints';
import FONTS from '@mlb/react-patterns/v4/style/fonts';
import { css } from 'styled-components';
import COLORS from '@mlb/react-patterns/v4/style/universal.color';
import GDBREAKPOINTS, { GDBREAKPOINTS_NUM } from '@gameday/components/themes/breakpoints';
import { BackdropContainer } from '@mlb/react-patterns/v4/components/Modal/Backdrop/backdrop.style';
import { CustomVerticalModalContent } from '@gameday/components/Overlay/overlay.style';
import { StyledBaseSkeleton } from '@mlb/react-patterns/v4/components/Skeleton/skeleton.style';

const {
    mlbWhite,
    mlbGrey4,
    mlbBlack,
    wrapBlue
} = COLORS;

const accentBlue = '#0F6DF0';

const {
    xs,
    sm,
    md,
    lg,
    xl
} = BREAKPOINTS;

const isSingleCol = `${GDBREAKPOINTS_NUM.MD - 1}px`;

export const ballColors = {
    ballColorStrike: 'rgba(198, 27, 43, 1)',
    ballColorBall: 'rgba(9, 131, 20, 1)',
    ballColorInPlay: 'rgba(0, 98, 227, 1)',
    ballColorInPlayOuts: 'rgba(119, 86, 179, 1)',
    ballStrokeColor: 'rgba(255, 255, 255, 0.8)',
    ballTextColor: 'rgba(255, 255, 255, 1)'
};

export const PREFIX_PATTERNS = 'patterns';
export const PREFIX = 'gameday';

const BaseStyles = css`
  :scope {
    --${PREFIX}-primary-font: ${FONTS.primaryFont};

    --${PREFIX_PATTERNS}-breakpoints-xs: ${xs};
    --${PREFIX_PATTERNS}-breakpoints-sm: ${sm};
    --${PREFIX_PATTERNS}-breakpoints-md: ${md};
    --${PREFIX_PATTERNS}-breakpoints-lg: ${lg};
    --${PREFIX_PATTERNS}-breakpoints-xl: ${xl};
    --${PREFIX_PATTERNS}-breakpoints-1280: 1280px;


    --${PREFIX}-breakpoints-xs: ${GDBREAKPOINTS.xs};
    --${PREFIX}-breakpoints-sm: ${GDBREAKPOINTS.sm};
    --${PREFIX}-breakpoints-md: ${GDBREAKPOINTS.md};
    --${PREFIX}-breakpoints-lg: ${GDBREAKPOINTS.lg};
    --${PREFIX}-breakpoints-xl: ${GDBREAKPOINTS.xl};
    --${PREFIX}-breakpoints-xl: ${GDBREAKPOINTS.xxl};

    --${PREFIX}-transparent: rgba(0, 0, 0, 0);

    --${PREFIX}-video-carousel-height: 168px;

    --${PREFIX}-gamestory-height: 121px;

    --${PREFIX}-video-carousel-shadow-color: rgba(0, 0, 0, 0.25);
    --${PREFIX}-video-carousel-play-icon-color: rgba(255, 255, 255, .75);
    --${PREFIX}-video-carousel-icon-color: rgba(255, 255, 255, 1.0);
    --${PREFIX}-video-highlight-gradient: linear-gradient(180deg, rgba(0, 0, 0, 0) 42.65%, #000000 100%);

    --${PREFIX}-game-story-gradient: linear-gradient(180deg, rgba(0, 0, 0, 0) 42.65%, #000000 100%);

    --${PREFIX}-top-performer-position-color: ${mlbGrey4};

    --${PREFIX}-wrap-section-title-color: ${mlbGrey4};

    --${PREFIX}-two-way-top-performers-label: ${mlbGrey4};

    --${PREFIX}-tabs-unselected-color: #949494;

    --${PREFIX}-section-padding: 24px;
    --${PREFIX}-section-padding-sm: 16px;
    --${PREFIX}-border-radius: 8px;
    --${PREFIX}-section-margin-sm: 16px;

    --${PREFIX}-section-padding-auto: var(--${PREFIX}-section-padding);

    --${PREFIX}-scaffold-box-shadow: 0px 3px 5px 0px rgba(50, 50, 50, 0.15);
    --${PREFIX}-scaffold-flexMain-min-width: 1250px;
    --${PREFIX}-scaffold-rail-height: 718px;
    --${PREFIX}-scaffold-gdapp-min-height: 513px;

    --${PREFIX}-panel-button-color: ${mlbGrey4};

    --${PREFIX}-card-title-font-size: 20px;
    --${PREFIX}-card-title-line-height: 20px;
    --${PREFIX}-card-title-font-weight: bold;

    --${PREFIX}-preview-card-title-font-size: 24px;
    --${PREFIX}-preview-card-title-line-height: 24px;

    /* Ball Colors */
    --${PREFIX}-ball-color-ball: ${ballColors.ballColorBall};
    --${PREFIX}-ball-color-strike: ${ballColors.ballColorStrike};
    --${PREFIX}-ball-color-in-play: ${ballColors.ballColorInPlay};
    --${PREFIX}-ball-color-in-play-outs: ${ballColors.ballColorInPlayOuts};
    --${PREFIX}-ball-stroke-color: ${ballColors.ballStrokeColor};
    --${PREFIX}-ball-text-color: ${ballColors.ballTextColor};

    --${PREFIX}-video-control-text-color: ${mlbBlack};

    @media (max-width: ${md}) {
        --${PREFIX}-section-padding-auto: var(--${PREFIX}-section-padding-sm);
    }

    --${PREFIX}-responsive-pitch-fx-message-color: #333333;

    --${PREFIX}-limited-functionality-chip-background: rgba(0, 0, 0, 0.5);

    --${PREFIX}-limited-functionality-chip-color: ${mlbWhite};

    /* safe-area-inset vars for handling iOS notches, home bars, app bars, etc... */
    @supports (padding-top: constant(safe-area-inset-top)) {
      --safe-area-inset-top: constant(safe-area-inset-top, 0);
      --safe-area-inset-bottom: constant(safe-area-inset-bottom, 0);
    }
    @supports (padding-top: env(safe-area-inset-top)) {
      --safe-area-inset-top: env(safe-area-inset-top, 0);
      --safe-area-inset-bottom: env(safe-area-inset-bottom, 0);
    }
    --${PREFIX}-summary-button-text-color: ${mlbWhite};

    --${PREFIX}-potg-badge-text-color:${mlbWhite};
    --${PREFIX}-potg-badge-background-color: ${wrapBlue};
    --${PREFIX}-potg-accent-color: ${wrapBlue};
    --${PREFIX}-top-performers-accent-color: ${accentBlue};



    body {
        line-height: 1.4em;
        font-size: 16px;
        margin: 0;
        margin-top: var(--safe-area-inset-top, 0);
        margin-bottom: var(--safe-area-inset-bottom, 0);

        #gameday-index-component__root {
          min-height: calc(100svh - var(--safe-area-inset-bottom)) !important;
        }
    }

    ${StyledBaseSkeleton} {
      background: linear-gradient(90deg, var(--gameday-skeleton-background), var(--gameday-skeleton-highlight), var(--gameday-skeleton-background));
      background-color: var(--gameday-skeleton-background);
      background-size: 200px 100%;
      background-repeat: no-repeat;
    }

    .pitchFx-overlay {
      ${CustomVerticalModalContent} {
        height: 163px;
        padding-bottom: var(--safe-area-inset-bottom);
      }
    };

    .responsive-pitch-fx-data-description {
      @media (max-width: ${isSingleCol}) {
        width:0;
        height:0;
      }
      transition: all 150ms linear;
      visibility: hidden;
      width: auto;
      opacity: 0;
      color: var(--gameday-primary-font-color);
      background: var(--gameday-background-color);
      font-family: var(--gameday-primary-font);
      font-size: 14px;
      line-height: 14px;
      text-align: left;
      z-index: -9999;
      padding: 16px;
      box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
      border-radius: 6px;
      :after {
        z-index: -1;
        position: absolute;
        top: 98.1%;
        left: 70%;
        margin-left: -24%;
        content: '';
        width: 0;
        height: 0;
        border-top: solid 12px var(--gameday-background-color);
        border-left: solid 10px transparent;
        border-right: solid 10px transparent;
      }

      .responsive-pitch-fx-data-item {
         height: 14px;
         margin-top: 8px;
         width: 210px;

         @media (max-width: ${isSingleCol}) {
          width:0;
          height:0;
         }

         .label {
           display: inline-block;
           width: 86px;
           color: var(--gameday-secondary-font-color);
           font-weight: 700;
         }
         .data-point {
          display: inline-block;
          font-weight: 700;
        }
        &:first-child {
          margin-top:0;
          span {
            &.label {
              width: auto;
            }
            &.data-point {
              &::before {
                content: "|";
                margin: 0 5px;
                color: var(--gameday-divider-color);
                font-size: 14px;
                line-height: 16px;
              }
            }
          }
        }
      }
    }
  }

  ${BackdropContainer} {
    opacity: 0.35 !important;
  }


`;

export default BaseStyles;
